import { z } from 'zod';

/** Categorías de plantilla de Meta: definen el precio de cada mensaje fuera de la ventana de 24 h. */
export const WHATSAPP_TEMPLATE_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'] as const;
export type WhatsappTemplateCategory = (typeof WHATSAPP_TEMPLATE_CATEGORIES)[number];
export const WHATSAPP_TEMPLATE_CATEGORY_LABELS: Record<WhatsappTemplateCategory, string> = {
  UTILITY: 'Utilidad (seguimiento, recordatorios)',
  MARKETING: 'Marketing (promociones, novedades)',
  AUTHENTICATION: 'Autenticación (códigos)',
};

const emptyToNull = (v: string | null | undefined) => (v && v.trim() ? v.trim() : null);

export const whatsappTemplateSchema = z.object({
  companyId: z.string().uuid(),
  name: z.string().trim().min(2).max(60),
  /** Texto con {nombre}, {proyecto}, {asesor}, {empresa}. */
  body: z.string().trim().min(5).max(1000),
  sortOrder: z.coerce.number().int().min(0).default(0),
  active: z.boolean().default(true),
  /** Nombre de la plantilla aprobada en Meta; vacío = solo sirve para wa.me y modo simulado. */
  metaName: z
    .string()
    .max(512)
    .optional()
    .nullable()
    .transform(emptyToNull)
    .refine(
      (v) => v === null || /^[a-z0-9_]+$/.test(v),
      'Usa solo minúsculas, números y guion bajo, igual que en Meta',
    ),
  metaLanguage: z.string().trim().min(2).max(10).default('es'),
  category: z.enum(WHATSAPP_TEMPLATE_CATEGORIES).default('UTILITY'),
});
export type WhatsappTemplateInput = z.infer<typeof whatsappTemplateSchema>;
export type WhatsappTemplatePayload = z.input<typeof whatsappTemplateSchema>;

export interface WhatsappTemplateDto extends WhatsappTemplateInput {
  id: string;
}

/** Rellena los marcadores de una plantilla. */
export function renderTemplate(
  body: string,
  vars: Record<string, string | null | undefined>,
): string {
  return body.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? '');
}

/** Valores de los marcadores en el orden en que aparecen: en Meta son {{1}}, {{2}}… */
export function templateParams(
  body: string,
  vars: Record<string, string | null | undefined>,
): string[] {
  return Array.from(body.matchAll(/\{(\w+)\}/g), (m) => vars[m[1]!] ?? '');
}

export const whatsappOpenSchema = z.object({
  templateId: z.string().uuid().optional().nullable(),
  /** Texto final enviado (para dejar registro). */
  text: z.string().max(1000).optional().nullable(),
});

/* ---------- Número propio de la empresa (Fase 8.4) ---------- */

export const WHATSAPP_ACCOUNT_MODES = ['SIMULADO', 'REAL'] as const;
export type WhatsappAccountMode = (typeof WHATSAPP_ACCOUNT_MODES)[number];
export const WHATSAPP_ACCOUNT_MODE_LABELS: Record<WhatsappAccountMode, string> = {
  SIMULADO: 'Simulado (no envía a WhatsApp; sirve para probar)',
  REAL: 'Real (WhatsApp Cloud API de Meta)',
};

export const whatsappAccountSchema = z
  .object({
    companyId: z.string().uuid(),
    /** Nombre que verá el cliente (normalmente el de la inmobiliaria). */
    displayName: z.string().trim().min(2).max(80),
    /** Número en formato internacional, p. ej. +51 999 888 777. */
    phoneNumber: z.string().trim().min(6).max(30),
    /** phone_number_id que muestra Meta en WhatsApp → Configuración de la API. */
    phoneNumberId: z.string().max(40).optional().nullable().transform(emptyToNull),
    /** Identificador de la cuenta de WhatsApp Business (WABA). */
    wabaId: z.string().max(40).optional().nullable().transform(emptyToNull),
    /** Token permanente del usuario del sistema; vacío = conservar el actual. */
    accessToken: z.string().max(600).optional().nullable().transform(emptyToNull),
    mode: z.enum(WHATSAPP_ACCOUNT_MODES).default('SIMULADO'),
  })
  .refine((v) => v.mode === 'SIMULADO' || !!v.phoneNumberId, {
    message: 'Para el modo real necesitas el phone_number_id de Meta',
    path: ['phoneNumberId'],
  });
export type WhatsappAccountInput = z.infer<typeof whatsappAccountSchema>;
export type WhatsappAccountPayload = z.input<typeof whatsappAccountSchema>;

export type WhatsappAccountStatus = 'SIN_PROBAR' | 'OK' | 'ERROR';

export interface WhatsappAccountDto {
  id: string;
  companyId: string;
  displayName: string;
  phoneNumber: string;
  phoneNumberId: string | null;
  wabaId: string | null;
  hasToken: boolean;
  tokenLast4: string | null;
  mode: WhatsappAccountMode;
  status: WhatsappAccountStatus;
  lastCheckAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

export interface WhatsappAccountTestResult {
  ok: boolean;
  mode: WhatsappAccountMode;
  message: string;
  verifiedName: string | null;
  displayPhoneNumber: string | null;
}

/* ---------- Envío desde la ficha ---------- */

export const whatsappSendSchema = z
  .object({
    templateId: z.string().uuid().optional().nullable(),
    text: z.string().trim().max(4000).optional().nullable(),
  })
  .refine((v) => !!v.templateId || !!(v.text && v.text.length > 0), {
    message: 'Escribe un mensaje o elige una plantilla',
    path: ['text'],
  });
export type WhatsappSendInput = z.infer<typeof whatsappSendSchema>;
export type WhatsappSendPayload = z.input<typeof whatsappSendSchema>;

/** Ventana de servicio de WhatsApp: texto libre solo si el cliente escribió en las últimas 24 h. */
export const WHATSAPP_WINDOW_HOURS = 24;

export function serviceWindowUntil(
  lastInboundAt: Date | string | null | undefined,
  now: Date = new Date(),
): Date | null {
  if (!lastInboundAt) return null;
  const until = new Date(new Date(lastInboundAt).getTime() + WHATSAPP_WINDOW_HOURS * 3_600_000);
  return until > now ? until : null;
}

/** Lo que la ficha necesita para mostrar el chat: si hay número, en qué modo y si la ventana está abierta. */
export interface WhatsappLeadContext {
  /** El bot de la empresa está encendido para conversar con clientes reales (8.5b). */
  botActive: boolean;
  configured: boolean;
  mode: WhatsappAccountMode | null;
  displayName: string | null;
  phoneNumber: string | null;
  contactPhone: string | null;
  contactable: boolean;
  lastInboundAt: string | null;
  windowOpenUntil: string | null;
}

export type MessageStatus = 'PENDIENTE' | 'ENVIADO' | 'ENTREGADO' | 'LEIDO' | 'FALLIDO';
export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  PENDIENTE: 'Enviando',
  ENVIADO: 'Enviado',
  ENTREGADO: 'Entregado',
  LEIDO: 'Leído',
  FALLIDO: 'No se entregó',
};
